import styled from 'styled-components';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { strings } from '/imports/strings';
import { isMobile, randomString } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { PublicFooter } from '/imports/ui/PublicFooter';
import { SignedOutPageStyled } from '/imports/ui/SignedOutPage';
import { Logo } from '/imports/ui/Logo';
import { toastOnError } from '/imports/ui/utils';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { Icon } from '/imports/ui/Icon';
import { sdgeUtilityAccountConnection, pgeUtilityAccountConnection, sceUtilityAccountConnection, UtilityAccount, utilityAccountType, utilityOptions, updateUtilityAccountMfaCode } from '/imports/api/utilityAccounts';
import { Select } from '/imports/ui/Select';
import { DateInput } from '/imports/ui/DateInput';
import { useFindOne, useSubscribe } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { RealtimeUpdates } from '/imports/api/realtimeUpdates';
import { css } from '/imports/ui/css';

export const UtilityAccountIntakePageStyled = styled(SignedOutPageStyled)`
  .logo {
    max-width: 100%;
    img {
      width: 100%;
    }
  }
  .inputs {
    label {
      margin-top: 1rem;
    }
  }
  .disclaimer {
    margin-top: 2rem;
  }
  .dataguard {
    width: 250px;
    max-width: 100%;
    display: block;
    margin: 0 auto;
    margin-top: 1rem;;
  }
  .error-message {
    color: ${css.red1};
    font-weight: bold;
    width: 30rem;
    text-align: center;
  }
  label {
    .icon {
      position: relative;
      top: 0.25rem;
      svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
`;

/**
 * Public intake page where a customer enters utility credentials, handling MFA prompts and connection status.
 */
export const UtilityAccountIntakePage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const _id = params.get('_id');
  if (!_id) return <div>Invalid link</div>;
  const hid = params.get('hid') || undefined;
  const [update, setUpdate] = useState<Partial<UtilityAccount>>({ _id });
  const [error, setError] = useState('');
  const [mfaCode, setMfaCode] = useState<string>();
  const [connecting, setConnecting] = useState(false);
  const [done, setDone] = useState(false);
  const [rtid,] = useState(`${randomString(17)}`);
  useSubscribe(Publications.realtimeUpdate, { _id: rtid });
  const realtimeUpdate = useFindOne(() => RealtimeUpdates.find({ _id: rtid }), [rtid]);
  /**
   * Submits credentials and starts the utility connection for the selected provider.
   * @param e - form submit event
   */
  const onSubmit = toastOnError(async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { _id, username, password, type, expireAt, email } = update;
    if (!_id || !username || !password || !type || !email) return;
    setConnecting(true);
    if (type === 'sdge') await sdgeUtilityAccountConnection.call({ _id, email, username, password, type, expireAt, rtid, hid });
    else if (type === 'pge') await pgeUtilityAccountConnection.call({ _id, email, username, password, type, expireAt, rtid, hid });
    else if (type === 'sce') await sceUtilityAccountConnection.call({ _id, email, username, password, type, expireAt, rtid, hid });
    setError('');
    setConnecting(false);
    setDone(true);
  });
  /**
   * Submits the MFA code entered by the user.
   * @param e - form submit event
   */
  const submitMfaCode = toastOnError(async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mfaCode) return;
    await updateUtilityAccountMfaCode.call({ _id, mfaCode });
  });
  if (connecting && (realtimeUpdate?.type === 'sdgeMfa' || realtimeUpdate?.type === 'pgeMfa' || realtimeUpdate?.type === 'sceMfa')) {
    return (
      <UtilityAccountIntakePageStyled className="page">
        <div className="wrapper">
          <div className="inner-wrapper">
            <div><Logo size={isMobile() ? 3 : 5} /></div>
            <div>
              <div>{realtimeUpdate.message}<br />&nbsp;</div>
            </div>
            <form onSubmit={submitMfaCode}>
              <div className="inputs">
                <Input id="mfa-code" value={mfaCode} onChange={(mfaCode) => setMfaCode(mfaCode)} placeholder='Enter the code and submit' />
                {error && <div className="form-error">{error}</div>}
                <div className="links">
                </div>
              </div>
              <div className="buttons">
                <Button
                  id="mfa-submit" type="submit"
                  disabled={!mfaCode}
                >
                  {strings.Submit()}
                </Button>
              </div>
            </form>
          </div>
        </div>
        <PublicFooter />
      </UtilityAccountIntakePageStyled>
    );
  }
  if (connecting && realtimeUpdate?.type === 'error') {
    return (
      <UtilityAccountIntakePageStyled className="page">
        <div className="wrapper">
          <div className="inner-wrapper">
            <div><Logo size={isMobile() ? 3 : 5} /></div>
            <div style={{ textAlign: 'center' }}>
              <Icon icon="warning" size={80} />
            </div>
            <div className="error-message">{realtimeUpdate.message}</div>
          </div>
        </div>
        <PublicFooter />
      </UtilityAccountIntakePageStyled>
    );
  }
  if (connecting) return <LoadingFullScreen caption={realtimeUpdate?.message || strings.ConnectingYourAccount()} />;
  if (done) {
    return (
      <UtilityAccountIntakePageStyled className="page">
        <div className="wrapper">
          <div className="inner-wrapper">
            <div><Logo size={isMobile() ? 3 : 5} /></div>
            <div>
              <div dangerouslySetInnerHTML={{ __html: strings.UtilityIntakeDoneMessage() }} />
            </div>
          </div>
        </div>
        <PublicFooter />
      </UtilityAccountIntakePageStyled>
    );
  }
  return (
    <UtilityAccountIntakePageStyled className="page">
      <div className="wrapper">
        <div className="inner-wrapper">
          <div><Logo size={isMobile() ? 3 : 5} /></div>
          <div>
            <div>{strings.UtilityIntakeDisclaimerMessage()}</div>
          </div>
          <form onSubmit={onSubmit}>
            <div className="inputs">
              <label>{strings.UtilityProvider()}</label>
              <Select id="utility-type" value={update.type}
                onChange={(type: any) => setUpdate({ ...update, type })} options={utilityOptions} />
              {
                update.type ?
                  <>
                    <label>{strings.Email()} <Icon icon="info" tip={strings.UtilityIntakeEmailTip()} /></label>
                    <Input inputId="account-email" value={update.email}
                      onChange={(email) => setUpdate({ ...update, email })} />
                    <label>{utilityAccountType({ type: update.type })} {strings.Username()}</label>
                    <Input inputId="account-username" value={update.username}
                      onChange={(username) => setUpdate({ ...update, username })} />
                    <label>{utilityAccountType({ type: update.type })} {strings.Password()}</label>
                    <Input inputId="account-password" type="password" value={update.password}
                      onChange={(password) => setUpdate({ ...update, password })} autoComplete='off' />
                    <label>{strings.AccessExpires()}</label>
                    <DateInput inputId="account-expires" value={update.expireAt}
                      onChange={(expireAt) => setUpdate({ ...update, expireAt })} autoComplete='off' />
                    <div className="disclaimer" dangerouslySetInnerHTML={{ __html: strings.UtilityIntakeDisclaimer() }} />
                    <img className="dataguard" src="/images/dataguard_logo.png" alt="" />
                  </>
                  :
                  null
              }
              {error && <div className="form-error">{error}</div>}
              <div className="links">
              </div>
            </div>
            <div className="buttons">
              <Button
                id="account-submit" type="submit"
                className={`btn ${connecting ? 'tertiary' : 'primary'}`}
                disabled={!update.type || !update.email || !update.username || !update.password}
              >
                {strings.Submit()}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <PublicFooter />
    </UtilityAccountIntakePageStyled>
  );
};
